import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface UserAuth {
    schoomyersId: string | null;
    nickname: string | null;
    email: string | null;
    selfIntroduction: string | null;
}

interface UserScore {
    achievementScore: number | null;
    totalMedalScore: number | null;
    totalBadgeScore: number | null;
    gearPostCount: number | null;
    gearLikeCount: number | null;
    festaEntryCheckinCount: number | null;
    totalScore: number | null;
    ownedLabCount: number | null;
    editorLaunchCount: number | null;
    whiteTeacherAnswerCount: number | null;
    editorWriteCount: number | null;
}

interface UserRegistrationStatus {
    profile?: number;
    address?: number;
    email?: number;
    sub_email?: number;
    phone_number?: number;
    password?: number;
    payment?: number;
    service?: number;
    family?: number;
}

interface UserMedal {
    medalCd: string | null;
    achievementScore: number | null;
    userSignatureMedalUrl: string | null;
    medalName: string | null;
}

interface UserBadge {
    badgeCd: string | null;
    achievementScore: number | null;
    awardDateTime: string | null;
}

interface UserSpot {
    hubId: number | null;
    organizationName: string | null;
    branchName: string | null;
}

type HydrationStatus = 'hydrating' | 'hydrated';

interface AuthState {
    _version: number;
    isAuthenticated: boolean;
    userAuth: UserAuth | null;
    userAvatarUrl: string | null;
    userMedal: UserMedal | null;
    userBadges: UserBadge[] | null;
    userScore: UserScore | null;
    userRegistrationStatus: UserRegistrationStatus;
    userSpots: UserSpot[] | null;
    redirectTo: string;
    hydration: HydrationStatus;
    needsServerLogout: boolean;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    setUserAuth: (userAuth: UserAuth | null) => void;
    setUserAvatarUrl: (url: string | null) => void;
    setUserMedal: (userMedal: UserMedal | null) => void;
    setUserBadges: (userBadges: UserBadge[] | null) => void;
    setUserScore: (userScore: UserScore | null) => void;
    setUserRegistrationStatus: (userRegistrationStatus: UserRegistrationStatus) => void;
    setUserSpots: (userSpots: UserSpot[] | null) => void;
    setRedirectTo: (url: string | '/') => void;
    setHydration: (v: HydrationStatus) => void;
    clearNeedsServerLogout: () => void;
    resetAuth: () => void;
}

const CURRENT_VERSION = 1;

const initialState = {
    _version: CURRENT_VERSION,
    isAuthenticated: false,
    userAuth: null,
    userAvatarUrl: null,
    userMedal: null,
    userBadges: null,
    userScore: null,
    userRegistrationStatus: {
        profile: 0,
        address: 0,
        email: 0,
        sub_email: 0,
        phone_number: 0,
        password: 0,
        payment: 0,
        service: 0,
        family: 0,
    },
    userSpots: null,
    redirectTo: '/',
    hydration: 'hydrating' as const,
    needsServerLogout: false,
};

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            ...initialState,
            setIsAuthenticated: (flag) => set({ isAuthenticated: flag }),
            setUserAuth: (userAuth) =>
                set({
                    userAuth,
                    isAuthenticated: !!userAuth?.schoomyersId,
                }),
            setUserAvatarUrl: (url) => set({ userAvatarUrl: url }),
            setUserMedal: (medal) => set({ userMedal: medal }),
            setUserBadges: (badges) => set({ userBadges: badges }),
            setUserScore: (score) => set({ userScore: score }),
            setUserRegistrationStatus: (status) =>
                set((state) => ({
                    userRegistrationStatus: {
                        ...state.userRegistrationStatus,
                        ...status,
                    },
                })),
            setUserSpots: (spots) => set({ userSpots: spots }),
            setRedirectTo: (url) => set({ redirectTo: url }),
            setHydration: (v) => set({ hydration: v }),
            clearNeedsServerLogout: () => set({ needsServerLogout: false }),
            resetAuth: () =>
                set((state) => ({
                    ...initialState,
                    hydration: state.hydration,
                })),
        }),
        {
            name: 'KEY-AUTH',
            storage: typeof window !== 'undefined' ? createJSONStorage(() => localStorage) : undefined,
            version: CURRENT_VERSION,
            migrate: (persistedState: any, version: number) => {
                if (version !== CURRENT_VERSION) {
                    return {
                        ...initialState,
                        needsServerLogout: true,
                    } as AuthState;
                }
                return persistedState as AuthState;
            },
            onRehydrateStorage: () => (state) => {
                state?.setHydration('hydrated');
            },
        },
    ),
);
